import styled from "@emotion/styled"
import {
  ApartmentOutlined,
  HomeOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { Badge, Card, Empty, Input, Skeleton, Tag, Typography } from "antd"
import { useGetFactoryStore } from "hooks/useFactoryDepartmentStock"
import { useMemo, useState } from "react"
import { FactoryWarehouseStore } from "services/interface"
import { stringToCommaFormat } from "utils/nuberFormat"
import StockTable from "./StockTable"

const isCentralWarehouse = (wh: FactoryWarehouseStore) =>
  wh.warehouse.warehouse_type === "central" ||
  wh.warehouse.department_master_id === null

const getWarehouseLabel = (wh: FactoryWarehouseStore) =>
  wh.department?.description || wh.warehouse.name

const countProducts = (wh: FactoryWarehouseStore) =>
  new Set(wh.stocks.map((s) => s.product_id)).size

const WarehouseListTab = () => {
  const { data, isLoading } = useGetFactoryStore()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  // เรียงคลังกลางขึ้นก่อนเสมอ ตามด้วยคลังแผนกเรียงตามชื่อ
  const warehouses = useMemo(() => {
    const list = data?.data.warehouses ?? []
    return [...list].sort((a, b) => {
      const aCentral = isCentralWarehouse(a) ? 0 : 1
      const bCentral = isCentralWarehouse(b) ? 0 : 1
      if (aCentral !== bCentral) return aCentral - bCentral
      return getWarehouseLabel(a).localeCompare(getWarehouseLabel(b), "th")
    })
  }, [data])

  const filteredWarehouses = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return warehouses
    return warehouses.filter((wh) =>
      `${wh.warehouse.name} ${wh.department?.description ?? ""}`
        .toLowerCase()
        .includes(keyword)
    )
  }, [warehouses, search])

  const selected =
    warehouses.find((wh) => wh.warehouse.id === selectedId) ??
    filteredWarehouses[0] ??
    warehouses[0]

  if (isLoading) {
    return <Skeleton active />
  }

  if (warehouses.length === 0) {
    return (
      <Card size="small" bordered>
        <Typography.Text type="secondary">
          ไม่พบคลังของโรงพยาบาล
        </Typography.Text>
      </Card>
    )
  }

  return (
    <Layout>
      <SidePanel>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="ค้นหาคลัง / แผนก"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <WarehouseList>
          {filteredWarehouses.length === 0 && (
            <Empty
              description="ไม่พบคลังที่ค้นหา"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 16 }}
            />
          )}
          {filteredWarehouses.map((wh) => {
            const central = isCentralWarehouse(wh)
            const active = selected?.warehouse.id === wh.warehouse.id
            return (
              <WarehouseItem
                key={wh.warehouse.id}
                active={active}
                central={central}
                onClick={() => setSelectedId(wh.warehouse.id)}
              >
                <ItemIcon active={active} central={central}>
                  {central ? <HomeOutlined /> : <ApartmentOutlined />}
                </ItemIcon>
                <ItemInfo>
                  <Typography.Text
                    strong
                    ellipsis={{ tooltip: getWarehouseLabel(wh) }}
                    style={{ fontSize: 14 }}
                  >
                    {getWarehouseLabel(wh)}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {central ? "คลังกลาง" : "คลังแผนก"} · {countProducts(wh)}{" "}
                    สินค้า
                  </Typography.Text>
                </ItemInfo>
                <ItemQty>
                  <Badge
                    showZero
                    overflowCount={9999}
                    count={wh.total_qty_onhand}
                    color={central ? "#1677ff" : "#8c8c8c"}
                  />
                </ItemQty>
              </WarehouseItem>
            )
          })}
        </WarehouseList>
      </SidePanel>

      <DetailPanel>
        {selected ? (
          <>
            <DetailHeader central={isCentralWarehouse(selected)}>
              <DetailTitle>
                <DetailIcon central={isCentralWarehouse(selected)}>
                  {isCentralWarehouse(selected) ? (
                    <HomeOutlined />
                  ) : (
                    <ApartmentOutlined />
                  )}
                </DetailIcon>
                <div>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {getWarehouseLabel(selected)}
                  </Typography.Title>
                  <div>
                    <Tag
                      color={isCentralWarehouse(selected) ? "geekblue" : "blue"}
                      style={{ marginTop: 4 }}
                    >
                      {isCentralWarehouse(selected) ? "คลังกลาง" : "คลังแผนก"}
                    </Tag>
                    {selected.department?.description &&
                      selected.department.description !==
                        getWarehouseLabel(selected) && (
                        <Tag color="cyan" style={{ marginTop: 4 }}>
                          {selected.department.description}
                        </Tag>
                      )}
                  </div>
                </div>
              </DetailTitle>
              <DetailStats>
                <StatBox>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    คงเหลือรวม
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: 18 }}>
                    {stringToCommaFormat(String(selected.total_qty_onhand))}
                  </Typography.Text>
                </StatBox>
                {/* <StatBox>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    จองแล้ว
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: 18 }}>
                    {selected.total_qty_reserved}
                  </Typography.Text>
                </StatBox> */}
                <StatBox>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    จำนวนสินค้า
                  </Typography.Text>
                  <Typography.Text strong style={{ fontSize: 18 }}>
                    {countProducts(selected)}
                  </Typography.Text>
                </StatBox>
              </DetailStats>
            </DetailHeader>
            <StockTable
              key={selected.warehouse.id}
              data={selected.stocks}
              showSearch
            />
          </>
        ) : (
          <Empty description="เลือกคลังเพื่อดูสต็อก" style={{ padding: 40 }} />
        )}
      </DetailPanel>
    </Layout>
  )
}

export default WarehouseListTab

const Layout = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px 4px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 8px;

  @media (max-width: 768px) {
    width: 100%;
    position: static;
  }
`

const WarehouseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 2px;

  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    max-height: none;
    padding-bottom: 4px;
  }
`

const WarehouseItem = styled.div<{ active: boolean; central: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1.5px solid ${({ active }) => (active ? "#1677ff" : "#efefef")};
  background-color: ${({ active }) => (active ? "#e6f4ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #1677ff;
  }

  @media (max-width: 768px) {
    min-width: 220px;
    flex-shrink: 0;
  }
`

const ItemIcon = styled.div<{ active: boolean; central: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  font-size: 17px;
  color: ${({ central }) => (central ? "#1d39c4" : "#1677ff")};
  background-color: ${({ central }) => (central ? "#f0f5ff" : "#e6f4ff")};
`

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const ItemQty = styled.div`
  flex-shrink: 0;
`

const DetailPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const DetailHeader = styled.div<{ central: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${({ central }) => (central ? "#adc6ff" : "#91caff")};
  background-color: ${({ central }) => (central ? "#f0f5ff" : "#e6f4ff")};
`

const DetailTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const DetailIcon = styled.div<{ central: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
  font-size: 20px;
  color: #ffffff;
  background-color: ${({ central }) => (central ? "#1d39c4" : "#1677ff")};
`

const DetailStats = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #efefef;
`
